import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const AboutOfferSenseAI = () => {
  const navigate = useNavigate();
  const [expandedFaq, setExpandedFaq] = useState(null);

  const benefits = [
    {
      icon: 'FileText',
      title: 'Automated Contract Parsing',
      description: 'AI-powered extraction of key terms, dates, and conditions from any real estate contract format'
    },
    {
      icon: 'BarChart3',
      title: 'Intelligent Offer Comparison',
      description: 'Side-by-side analysis with visual indicators highlighting strengths and weaknesses of each offer'
    },
    {
      icon: 'FileCheck',
      title: 'Professional Report Generation',
      description: 'Generate branded PDF reports with comprehensive offer analysis for clients in seconds'
    },
    {
      icon: 'Clock',
      title: 'Save Hours Per Transaction',
      description: 'Reduce contract review time from hours to minutes with automated data extraction'
    },
    {
      icon: 'Shield',
      title: 'Reduce Risk & Errors',
      description: 'Never miss critical contract details with AI-powered verification and validation'
    },
    {
      icon: 'Users',
      title: 'Impress Your Clients',
      description: 'Deliver professional analysis reports that showcase your expertise and attention to detail'
    }
  ];

  const pricingTiers = [
    {
      name: 'Free',
      price: '$0',
      period: 'forever',
      description: 'Perfect for trying out OfferSense',
      features: [
        { text: '1 active property', included: true },
        { text: 'Up to 3 offers per property', included: true },
        { text: 'Basic contract parsing', included: true },
        { text: 'Standard offer comparison', included: true },
        { text: 'Email support', included: true },
        { text: 'Professional PDF reports', included: false },
        { text: 'Advanced AI analysis', included: false },
        { text: 'Priority support', included: false }
      ],
      cta: 'Start Free',
      variant: 'outline',
      popular: false
    },
    {
      name: 'Professional',
      price: '$29',
      period: 'per month',
      description: 'For active real estate agents',
      features: [
        { text: 'Unlimited active properties', included: true },
        { text: 'Unlimited offers', included: true },
        { text: 'Advanced AI contract parsing', included: true },
        { text: 'Intelligent offer comparison', included: true },
        { text: 'Professional PDF reports', included: true },
        { text: 'Advanced AI analysis', included: true },
        { text: 'Priority email support', included: true },
        { text: 'Custom branding', included: true }
      ],
      cta: 'Start Free Trial',
      variant: 'primary',
      popular: true
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      period: 'contact us',
      description: 'For teams and brokerages',
      features: [
        { text: 'Everything in Professional', included: true },
        { text: 'Multi-agent team accounts', included: true },
        { text: 'Dedicated account manager', included: true },
        { text: 'Custom integrations', included: true },
        { text: 'White-label options', included: true },
        { text: 'Advanced analytics', included: true },
        { text: 'Phone & chat support', included: true },
        { text: 'SLA guarantee', included: true }
      ],
      cta: 'Contact Sales',
      variant: 'outline',
      popular: false
    }
  ];

  const testimonials = [
    {
      quote: "OfferSense has transformed how I present offers to my clients. What used to take me 2-3 hours now takes 15 minutes. The AI analysis catches details I might have missed.",
      author: "Sarah Mitchell",
      role: "Luxury Real Estate Agent",
      location: "Beverly Hills, CA",
      image: null
    },
    {
      quote: "The professional reports have elevated my service offering. Clients are impressed with the detailed analysis, and I've closed deals faster because everyone understands the offers clearly.",
      author: "Michael Chen",
      role: "Broker Associate",
      location: "Austin, TX",
      image: null
    },
    {
      quote: "As a busy agent handling multiple properties, OfferSense keeps me organized and ensures I never miss important contract details. It's become essential to my workflow.",
      author: "Jennifer Rodriguez",
      role: "Top Producer",
      location: "Miami, FL",
      image: null
    }
  ];

  const faqs = [
    {
      question: 'How does the AI contract parsing work?',
      answer: 'OfferSense uses advanced AI models trained specifically on real estate contracts to automatically extract key information including purchase price, financing terms, contingencies, closing dates, and more. Simply upload a PDF contract and our AI handles the rest.'
    },
    {
      question: 'Can I try OfferSense before committing to a paid plan?',
      answer: 'Absolutely! Our Free plan lets you manage 1 property with up to 3 offers to experience the core functionality. Professional plan also includes a 14-day free trial with full access to all features.'
    },
    {
      question: 'What contract formats are supported?',
      answer: 'OfferSense supports PDF contracts from all major real estate forms including NAR, state-specific forms, and custom contracts. Our AI is continuously trained to recognize new formats.'
    },
    {
      question: 'How secure is my client data?',
      answer: 'Security is our top priority. All data is encrypted in transit and at rest, stored on enterprise-grade infrastructure, and we never share your information with third parties. We are fully compliant with data protection regulations.'
    },
    {
      question: 'Can I cancel my subscription anytime?',
      answer: 'Yes, you can cancel your subscription at any time with no penalties. Your access will continue until the end of your current billing period, and you can export all your data before canceling.'
    },
    {
      question: 'Do you offer team or brokerage pricing?',
      answer: 'Yes! Our Enterprise plan is designed for teams and brokerages with custom pricing based on the number of agents. Contact our sales team to discuss your specific needs and get a custom quote.'
    }
  ];

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const handleGetStarted = () => {
    navigate('/user-registration');
  };

  const handleLogin = () => {
    navigate('/user-login');
  };

  const handleContactSales = () => {
    window.location.href = 'mailto:sales@offersense.ai?subject=Enterprise Inquiry';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation Header */}
      <header className="border-b border-border bg-card sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/assets/images/90A19BF4-3446-4543-87E4-2148054C81C1-1768952058269.jpeg" alt="OfferSense Logo" className="w-10 h-10 object-contain" />
              <span className="text-xl font-semibold">
                <span className="text-[#2C3E5F]">OfferSense</span>
                <span className="text-[#5DBFB3]">.Ai</span>
              </span>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={handleLogin}>
                Login
              </Button>
              <Button variant="primary" size="sm" onClick={handleGetStarted}>
                Start Free Trial
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary/5 via-secondary/5 to-background py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Icon name="Sparkles" size={16} />
              <span>AI-Powered Real Estate Offer Management</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              Analyze Offers in Minutes,
              <br />
              <span className="text-primary">Not Hours</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              OfferSense uses advanced AI to automatically parse contracts, compare offers side-by-side, and generate professional reports that impress your clients.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button variant="primary" size="lg" onClick={handleGetStarted} iconName="ArrowRight" iconPosition="right">
                Start Free Trial
              </Button>
              <Button variant="outline" size="lg" onClick={handleLogin}>
                Sign In
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              No credit card required • 14-day free trial • Cancel anytime
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Why Real Estate Agents Choose OfferSense
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Save time, reduce errors, and deliver exceptional service to your clients
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits?.map((benefit, index) => (
              <div key={index} className="bg-background rounded-xl p-6 border border-border hover:border-primary/50 transition-colors">
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                  <Icon name={benefit?.icon} size={24} className="text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-2">{benefit?.title}</h3>
                <p className="text-muted-foreground">{benefit?.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose the plan that fits your business. Upgrade or downgrade anytime.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingTiers?.map((tier, index) => (
              <div
                key={index}
                className={`bg-card rounded-xl p-8 border-2 transition-all hover:shadow-lg ${
                  tier?.popular ? 'border-primary shadow-elevation-md' : 'border-border'
                }`}
              >
                {tier?.popular && (
                  <div className="inline-flex items-center gap-1 bg-primary text-white px-3 py-1 rounded-full text-xs font-medium mb-4">
                    <Icon name="Star" size={12} />
                    <span>Most Popular</span>
                  </div>
                )}
                <h3 className="text-2xl font-bold text-foreground mb-2">{tier?.name}</h3>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-foreground">{tier?.price}</span>
                  {tier?.price !== 'Custom' && <span className="text-muted-foreground ml-2">/{tier?.period}</span>}
                  {tier?.price === 'Custom' && <span className="text-muted-foreground ml-2 text-lg">{tier?.period}</span>}
                </div>
                <p className="text-muted-foreground mb-6">{tier?.description}</p>
                <Button
                  variant={tier?.variant}
                  size="lg"
                  className="w-full mb-6"
                  onClick={tier?.name === 'Enterprise' ? handleContactSales : handleGetStarted}
                >
                  {tier?.cta}
                </Button>
                <ul className="space-y-3">
                  {tier?.features?.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start gap-3">
                      <Icon
                        name={feature?.included ? 'Check' : 'X'}
                        size={20}
                        className={feature?.included ? 'text-success mt-0.5' : 'text-muted-foreground mt-0.5'}
                      />
                      <span className={feature?.included ? 'text-foreground' : 'text-muted-foreground'}>
                        {feature?.text}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 md:py-24 bg-card">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Trusted by Top Real Estate Agents
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              See what agents are saying about OfferSense
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials?.map((testimonial, index) => (
              <div key={index} className="bg-background rounded-xl p-6 border border-border">
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)]?.map((_, i) => (
                    <Icon key={i} name="Star" size={16} className="text-warning fill-warning" />
                  ))}
                </div>
                <p className="text-foreground mb-6 italic">"{testimonial?.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="User" size={24} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{testimonial?.author}</p>
                    <p className="text-sm text-muted-foreground">{testimonial?.role}</p>
                    <p className="text-xs text-muted-foreground">{testimonial?.location}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-background">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-muted-foreground">
              Everything you need to know about OfferSense
            </p>
          </div>
          <div className="space-y-4">
            {faqs?.map((faq, index) => (
              <div key={index} className="bg-card rounded-xl border border-border overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-muted/50 transition-colors"
                >
                  <span className="font-semibold text-foreground pr-4">{faq?.question}</span>
                  <Icon
                    name={expandedFaq === index ? 'ChevronUp' : 'ChevronDown'}
                    size={20}
                    className="text-muted-foreground flex-shrink-0"
                  />
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-4 text-muted-foreground">
                    {faq?.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section className="py-16 md:py-24 bg-card">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Contact Us
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Have questions or need assistance? We're here to help!
            </p>
          </div>
          <div className="bg-background rounded-xl p-8 border border-border max-w-2xl mx-auto">
            <div className="flex flex-col items-center text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Icon name="Mail" size={32} className="text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-2">Email Support</h3>
              <p className="text-muted-foreground mb-4">
                Our support team typically responds within 24 hours
              </p>
              <a
                href="mailto:support@offersense.ai"
                className="text-primary hover:text-primary/80 font-medium text-lg transition-colors flex items-center gap-2"
              >
                <Icon name="Mail" size={20} />
                support@offersense.ai
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary to-secondary">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Transform Your Offer Management?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Join hundreds of real estate agents who are saving time and closing deals faster with OfferSense.AI
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              variant="outline"
              size="lg"
              onClick={handleGetStarted}
              className="bg-white text-primary hover:bg-white/90 border-white"
              iconName="ArrowRight"
              iconPosition="right"
            >
              Start Free Trial
            </Button>
            <Button
              variant="ghost"
              size="lg"
              onClick={handleContactSales}
              className="text-white hover:bg-white/10 border-white/30"
            >
              Contact Sales
            </Button>
          </div>
          <p className="text-sm text-white/80 mt-4">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border bg-card">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                   style={{ background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-secondary) 100%)' }}>
                <Icon name="TrendingUp" size={16} color="#FFFFFF" />
              </div>
              <span className="font-semibold text-foreground">OfferSense.AI</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} OfferSense. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Privacy Policy
              </a>
              <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Terms of Service
              </a>
              <a href="mailto:support@offersense.ai" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Contact
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutOfferSenseAI;